// The start screen's layer (03-startup-session.md, The start screen, v1.58.48): drawn by the served page
// before any script (build/tokens.ts), kept while a start screen is shown (`keepStart`), gone once the
// opened screen has drawn its first frame under it: a 200 ms fade (in one frame with less motion). A
// circle switch draws it again in the frame of the pick, in the top layer, at once (33, v1.58.48).
import { startScreenHtml } from './mark';

export type StartKind = 'start' | 'signin' | 'switch';

const layer = () => document.getElementById('desyr-start');
const still = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/** The layer, drawn again when the page didn't serve it (a sign-in's hand-over, a test). */
function drawn(kind?: StartKind): HTMLElement {
  let el = layer();
  if (!el) {
    const place = document.createElement('div');
    place.innerHTML = startScreenHtml;
    el = place.firstElementChild as HTMLElement;
    document.body.prepend(el);
  }
  el.classList.remove('going');
  if (kind) el.dataset.desyrSplash = kind;
  document.getElementById('root')?.setAttribute('aria-busy', 'true');
  return el;
}

/** What it opens: "start", "signin" (after a desyr ID sign-in) or "switch" (90). */
export const startKind = (): StartKind => (layer()?.dataset.desyrSplash as StartKind | undefined) ?? 'start';

/** Where its words go. */
export const startWords = () => drawn().querySelector<HTMLElement>('.start-words')!;

/** The sign-in step it stands for while the automatic desyr ID sign-in is prepared ("auto", 36, v1.58.51). */
export const startStep = (step?: string) => {
  const el = drawn();
  if (step) el.dataset.desyrSignInStep = step;
  else delete el.dataset.desyrSignInStep;
};

/** Its state hook: "waiting", "slow" from 8 s, "unreachable" (90). */
export const startState = (state: string) => void (drawn().dataset.desyrStart = state);

let since = 0;
/** When it began (performance.now() ms): the page's start, or the pick of a switch. */
export const startSince = () => since;

let keepers = 0;
/** Shown while kept; the last one let go, it goes after the screen under it has drawn a frame. */
export function keepStart(): () => void {
  keepers++;
  drawn();
  return () => {
    keepers--;
    requestAnimationFrame(() => requestAnimationFrame(() => keepers === 0 && go()));
  };
}

function go(): void {
  const el = layer();
  const gone = () => (el?.remove(), document.getElementById('root')?.removeAttribute('aria-busy'));
  if (!el || still()) return gone();
  if (el.classList.contains('going')) return;
  el.classList.add('going');
  setTimeout(() => keepers === 0 && gone(), 200);
}

/** The pick of another circle: the start screen over everything in this frame, at once. */
/** `line`: its quiet line after 1.5 s, drawn here too while a slow reload keeps this page on screen. */
export function coverForSwitch(line?: string): void {
  const el = drawn('switch');
  el.style.transition = 'none';
  el.setAttribute('popover', 'manual');
  el.showPopover?.();
  since = performance.now();
  if (line)
    setTimeout(() => {
      const words = startWords();
      if (words.childElementCount) return;
      const p = Object.assign(document.createElement('p'), { className: 'start-line', textContent: line });
      p.setAttribute('role', 'status');
      words.append(p);
    }, 1500);
}

/** At the page's start: a switch's words count from its pick (`at`, epoch ms), kept in `desyr_switching`. */
export function startFrom(at: number | undefined): void {
  if (at && startKind() === 'switch') since = performance.now() - (Date.now() - at);
}
